import { InjectionKey, reactive } from 'vue'
import { AxiosInstance } from 'axios'
import { PrefApi } from '@/api/common/pref'
import { HolidaysApi } from '@/api/common/holidays'
import { UsersApi } from '@/api/common/users'
import { CategoriesApi } from '@/api/common/categories'
import { ApiError } from '@/types/global'
import { IGlobalStore } from '@/types/common'
import { useCommonApiError } from '@/hooks/error/common'
import { CityApi } from '@/api/common/city'
import {
  InputRetainApi,
  type StoreInputRetainRequest,
  type LoadInputRetainRequest,
  type LoadInputRetainApiResponse,
  type DeleteInputRetainRequest,
} from '@/api/common/inputRetain'

export class GlobalStore {
  categoriesApi: CategoriesApi
  holidaysApi: HolidaysApi
  prefApi: PrefApi
  cityApi: CityApi
  usersApi: UsersApi
  inputRetainApi: InputRetainApi

  constructor(axios: AxiosInstance) {
    this.categoriesApi = new CategoriesApi(axios)
    this.prefApi = new PrefApi(axios)
    this.cityApi = new CityApi(axios)
    this.holidaysApi = new HolidaysApi(axios)
    this.usersApi = new UsersApi(axios)
    this.inputRetainApi = new InputRetainApi(axios)
  }

  public state = reactive<IGlobalStore>({
    categories: [],
    pref: [],
    city: [],
    holidays: [],
    userRole: 'guest',
  })

  /**
   * [API] 撮影ジャンル情報取得
   */
  public async getCategories(): Promise<void> {
    await this.categoriesApi
      .getCategories()
      .then((response) => {
        this.state.categories = response
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }

  /**
   * [API] 都道府県の情報を取得
   */
  public async getPref(): Promise<void> {
    await this.prefApi
      .getPref()
      .then((response) => {
        this.state.pref = response.pref
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }

  /**
   * [API] 市区町村の情報を取得
   */
  public async getCity(prefId: string): Promise<void> {
    await this.cityApi
      .postCity(prefId)
      .then((response) => {
        this.state.city = response.addresses
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }

  /**
   * [API] 祝日取得
   */
  public async getHolidays(): Promise<void> {
    await this.holidaysApi
      .getHolidays()
      .then((response) => {
        this.state.holidays = response
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }

  /**
   * [API] ユーザ情報取得取得
   */
  public async getUsersRole(): Promise<void> {
    await this.usersApi
      .getUsersRole()
      .then((response) => {
        this.state.userRole = response
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }

  /**
   * [API] 入力内容保存API
   * @param request
   */
  public async store3dSecureInputRetain<T extends Record<string, unknown>>(
    request: StoreInputRetainRequest<T>
  ): Promise<void> {
    await this.inputRetainApi.storeInputRetain<T>(request).catch(({ response }: ApiError<null>) => {
      useCommonApiError(response)
    })
  }

  /**
   * [API] 入力内容取得API
   * @param request
   */
  public async load3dSecureInputRetain<T extends Record<string, unknown>>(
    request: LoadInputRetainRequest
  ): Promise<LoadInputRetainApiResponse<T>['data'] | undefined> {
    return await this.inputRetainApi.loadInputRetain<T>(request).catch(({ response }: ApiError<null>) => {
      useCommonApiError(response)
      return undefined
    })
  }

  /**
   * [API] 入力内容削除API
   * @param request
   */
  public async delete3dSecureInputRetain(request: DeleteInputRetainRequest): Promise<void> {
    await this.inputRetainApi.deleteInputRetain(request).catch(({ response }: ApiError<null>) => {
      useCommonApiError(response)
    })
  }
}
export const GlobalStoreKey: InjectionKey<GlobalStore> = Symbol('GlobalStoreKey')
